import React, { useRef, useMemo, type Ref } from 'react'

import { TextInput } from '@atoms'
import debounce from '@utils/debounce'
import usePartialReload from '@hooks/usePartialReload'

export const searchFn = (
  event: React.ChangeEvent<HTMLInputElement>,
  partialReloadKeys: string[],
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const { name, value } = event.target
  const queryString = `${name}=${value}`
  usePartialReload(queryString, partialReloadKeys, setLoading)
}

export type SearchFieldProps = {
  displayName: string
  fieldName: string
  placeholder?: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  value?: string
  dataCompId?: string
  defaultValue?: string
  hideLabel?: boolean
  inputWrapperClassName?: string
  ref?: Ref<HTMLInputElement>
}

const SearchField = function SearchField({
  fieldName,
  displayName,
  placeholder,
  onChange,
  value,
  dataCompId,
  defaultValue,
  hideLabel = false,
  inputWrapperClassName,
  ref,
}: SearchFieldProps) {
  defaultValue = defaultValue ?? new URLSearchParams(window.location.search).get(fieldName) ?? ''

  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const onChangeFn = useMemo(
    () =>
      debounce((...args: Parameters<typeof onChange>) => {
        onChangeRef.current(...args)
      }, 300),
    []
  )

  return (
    <TextInput
      name={fieldName}
      type="search"
      displayName={displayName}
      placeholder={placeholder}
      {...(value === undefined ? { defaultValue } : { value })}
      onChange={value === undefined ? onChangeFn : onChange}
      hideLabel={hideLabel}
      inputWrapperClassName={inputWrapperClassName}
      leadingIcon="search-lg"
      ref={ref}
      {...(dataCompId && { 'data-compid': `${dataCompId}` })}
    />
  )
}

export default SearchField
